import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Wallet, ClipboardList, Users, ChevronRight, Share2, LogOut, Copy, Check } from 'lucide-react';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import AdBanner from '../components/AdBanner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function Home() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [family, setFamily] = useState(null);
    const [members, setMembers] = useState([]);
    const [copied, setCopied] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    useEffect(() => {
        api.get('/family/me').then((r) => {
            setFamily(r.data.family);
            setMembers(r.data.members || []);
        }).catch(() => {});
    }, []);

    const copyCode = async () => {
        if (!family?.code) return;
        await navigator.clipboard.writeText(family.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const firstName = user?.name?.split(' ')[0] || 'Familia';

    return (
        <div className="min-h-screen bg-surface app-shell pb-28">
            <div className="px-6">
                <TopBar onPremiumClick={() => alert('Próximamente: vista de planes Premium')} onBellClick={() => navigate('/notificaciones')} onMenuClick={() => setShowMenu(true)} />

                <div className="mt-2 fade-up">
                    <p className="text-gray-500 text-base">¡Hola {firstName}!</p>
                    <h1 className="text-3xl font-bold text-brand-navy tracking-tight" data-testid="home-family-name">
                        {family?.name || 'Tu familia'}
                    </h1>
                </div>

                {family && (
                    <div className="mt-4 bg-brand/5 border border-brand/15 rounded-2xl px-4 py-3 flex items-center justify-between fade-up fade-up-delay-1" data-testid="home-family-code-card">
                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Código de familia</div>
                            <div className="text-base font-bold text-brand">{family.code}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={copyCode} className="w-10 h-10 rounded-full hover:bg-brand/10 flex items-center justify-center" title="Copiar" data-testid="home-copy-code-btn">
                                {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-brand" />}
                            </button>
                            <button onClick={copyCode} className="w-10 h-10 rounded-full hover:bg-brand/10 flex items-center justify-center" title="Compartir" data-testid="home-share-code-btn">
                                <Share2 className="w-5 h-5 text-brand" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Quick actions / placeholder sections */}
                <div className="mt-6 space-y-3 fade-up fade-up-delay-2">
                    <SectionPlaceholder icon={Calendar} title="Agenda" subtitle="Eventos y recordatorios" color="bg-blue-100 text-blue-500" onClick={() => navigate('/agenda')} testId="home-section-agenda" />
                    <SectionPlaceholder icon={Wallet} title="Wallet" subtitle="Gastos, ahorros y cuentas" color="bg-emerald-100 text-emerald-500" onClick={() => navigate('/wallet')} testId="home-section-wallet" />
                    <SectionPlaceholder icon={ClipboardList} title="Listas" subtitle="Listas, notas y contactos" color="bg-orange-100 text-orange-500" onClick={() => navigate('/listas')} testId="home-section-listas" />
                    <SectionPlaceholder icon={Users} title={`Familia (${members.length})`} subtitle="Miembros del hogar" color="bg-pink-100 text-pink-500" onClick={() => navigate('/familia')} testId="home-section-familia" />
                </div>

                <div className="mt-6 fade-up fade-up-delay-3">
                    <div className="bg-brand/5 border border-brand/15 rounded-2xl p-4">
                        <h3 className="text-sm font-bold text-brand-navy mb-1">🎉 Bienvenido a 4everSync</h3>
                        <p className="text-xs text-gray-600 leading-relaxed">
                            Has completado la <strong>Fase 0</strong>. Las secciones de Agenda, Wallet, Listas y Familia (perfiles ricos) están siendo construidas. Por ahora puedes crear tu familia, invitar miembros y probar la app.
                        </p>
                    </div>
                </div>
            </div>

            <div className="fixed bottom-[72px] left-1/2 -translate-x-1/2 w-full max-w-[480px] z-30">
                <AdBanner />
            </div>

            <BottomNav />

            {showMenu && (
                <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowMenu(false)}>
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="relative w-72 h-full bg-white shadow-2xl p-6 fade-up" onClick={(e) => e.stopPropagation()} data-testid="side-menu">
                        <h3 className="text-lg font-bold text-brand-navy mb-4">{user?.name || 'Mi cuenta'}</h3>
                        <p className="text-xs text-gray-500 mb-4">{user?.email}</p>
                        <button onClick={() => { setShowMenu(false); logout(); navigate('/intro', { replace: true }); }} className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-2xl text-red-500 font-semibold" data-testid="menu-logout-btn">
                            <LogOut className="w-5 h-5" />
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionPlaceholder({ icon: Icon, title, subtitle, color, onClick, testId }) {
    return (
        <button onClick={onClick} className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 hover:shadow-card-md active:scale-[0.98] transition shadow-card" data-testid={testId}>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left">
                <h3 className="font-bold text-brand-navy">{title}</h3>
                <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
    );
}
